import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '../../Components/UI/DataTable';
import Button from '../../Components/UI/Button';
import DeleteConfirmatationModal from '../../Components/CommonComponents/DeleteConfirmatationModal';
import useTableData from '../../Components/CommonComponents/useTableData';
import { apiGet, apiDelete } from '../../Components/CommonComponents/tableApi';
import { downloadReturnPdf } from './orderPdf';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

/**
 * Returns screen.
 *
 * Mirrors OrdersTable: rows are returns, not medicine lines, so one return with
 * several damaged medicines is one row and one PDF.
 */

const formatDate = (value) => {
  if (!value) return 'NA';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'NA'
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const medicineSummary = (items = []) => {
  const names = items.map((item) => item?.nameOfMedicine).filter(Boolean);
  if (names.length === 0) return 'NA';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

export default function ReturnsTable() {
  const navigate = useNavigate();
  const [busyReturnId, setBusyReturnId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetcher = useCallback(
    (query) =>
      apiGet(
        `inventory/returns/grouped?search=${encodeURIComponent(query.search)}&page=${query.page}&limit=${query.limit}`
      ),
    []
  );

  const table = useTableData(fetcher);

  const handleDownload = async (returnOrder) => {
    setBusyReturnId(returnOrder?.Return_Id);
    const toastId = toast.loading('Preparing PDF...');
    try {
      await downloadReturnPdf(returnOrder);
      toast.dismiss(toastId);
      toast.success('Return PDF downloaded');
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Could not generate the return PDF');
      console.log('Return PDF failed', error);
    } finally {
      setBusyReturnId(null);
    }
  };

  // One spreadsheet row per medicine line of every return matching the search.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await apiGet(
        `inventory/returns/grouped?search=${encodeURIComponent(table.query.search || '')}&page=1&limit=100000`
      );
      const lines = (result?.data || []).flatMap((returnOrder) =>
        (returnOrder?.items || []).map((item) => ({ ...item, Return_Id: returnOrder.Return_Id, date: returnOrder.date }))
      );
      if (lines.length === 0) {
        toast.info('No returns to export');
        return;
      }
      exportToExcel(
        lines,
        'Returns',
        [
          { key: 'Return_Id', label: 'Return Id' },
          { key: 'date', label: 'Date', format: (v) => formatDate(v) },
          { key: 'order', label: 'Against Order' },
          { key: 'distributorName', label: 'Distributor' },
          { key: 'nameOfMedicine', label: 'Medicine' },
          { key: 'company', label: 'Company' },
          { key: 'potencyOrPower', label: 'Potency' },
          { key: 'quantity', label: 'Ordered Qty' },
          { key: 'damage', label: 'Damaged Qty' },
          { key: 'rate', label: 'Rate', format: (v) => (v ?? '') },
          { key: 'reason', label: 'Reason' },
        ],
        'Returns'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const headers = [
    { key: 'Return_Id', label: 'Return Id', render: (value) => <span className="font-medium text-slate-900">{value}</span> },
    { key: 'date', label: 'Date', render: (value) => formatDate(value) },
    { key: 'order', label: 'Against Order' },
    { key: 'distributorName', label: 'Distributor' },
    { key: 'items', label: 'Medicines', render: (value) => medicineSummary(value) },
    { key: 'itemCount', label: 'Items', align: 'right' },
    { key: 'totalDamage', label: 'Damaged', align: 'right' },
  ];

  return (
    <div className="w-full">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Returns</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" isLoading={isExporting} onClick={handleExport}>
            <FiDownload className="h-4 w-4" />
            Export to Excel
          </Button>
          <Button variant="secondary" onClick={() => navigate('/main/inventory/Returns/Returns/addNew')}>
            + Add Return
          </Button>
        </div>
      </div>

      <DataTable
        headers={headers}
        data={table.rows}
        isLoading={table.isLoading}
        emptyMessage="No return orders yet."
        page={table.query.page}
        totalPages={table.totalPages}
        rowsPerPage={table.query.limit}
        onPageChange={table.setPage}
        onRowsPerPageChange={table.setLimit}
        onSearch={table.setSearch}
        searchValue={table.query.search}
        searchPlaceholder="Search by return id, order id, medicine or distributor"
        actions={(returnOrder) => (
          <>
            <button
              type="button"
              disabled={busyReturnId === returnOrder?.Return_Id}
              onClick={() => handleDownload(returnOrder)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              <FiDownload className="h-3.5 w-3.5" />
              PDF
            </button>

            <DeleteConfirmatationModal
              deleteFunction={() =>
                apiDelete(`inventory/returns/group/${encodeURIComponent(returnOrder?.Return_Id)}`, 'Return Order', {
                  onDone: table.reload,
                })
              }
              text={`return ${returnOrder?.Return_Id}`}
              heading="Delete Return Order"
            />
          </>
        )}
      />

      <ToastContainer />
    </div>
  );
}
